<template>
  <div v-if="visible" class="plus-wf-anchor-layer">
    <div class="plus-wf-anchor-layer__backdrop" @click="$emit('close')"></div>
    <div class="plus-wf-anchor-layer__panel plus-wf-anchor-layer__panel--style" :style="panelPositionStyle" @click.stop>
      <div class="px-5 pt-5 pb-3 flex items-center justify-between gap-3">
        <div class="text-lg font-black text-gray-100 tracking-tight">风格库</div>
        <div class="flex items-center gap-2">
          <button
            v-if="selectedStyleLocal"
            type="button"
            class="h-8 px-3 rounded-full bg-white/[0.06] hover:bg-white/[0.10] border border-white/[0.10] text-[12px] text-gray-200 font-semibold"
            @click="clearStyle"
          >
            清除
          </button>
          <button
            type="button"
            class="w-9 h-9 rounded-full bg-white/[0.06] hover:bg-white/[0.10] border border-white/[0.10] text-gray-300 flex items-center justify-center"
            @click="$emit('close')"
          >
            <i class="fas fa-times text-sm"></i>
          </button>
        </div>
      </div>

      <div class="px-5 pb-3">
        <div class="flex flex-wrap gap-2">
          <button
            v-for="t in styleCategoryTabs"
            :key="t.id"
            type="button"
            class="h-8 px-3 rounded-full border text-[12px] font-semibold transition-colors"
            :class="
              styleTab === t.id
                ? 'bg-white/[0.12] text-white border-white/[0.14]'
                : 'bg-white/[0.06] hover:bg-white/[0.10] text-gray-300 border-white/[0.10]'
            "
            @click="onStyleCategoryTabClick(t)"
          >
            {{ t.label }}
          </button>
        </div>
      </div>

      <div
        ref="styleLibraryScroll"
        class="px-5 pb-5 flex-1 min-h-0 overflow-y-auto"
        @scroll="onStyleLibraryScroll"
      >
        <div v-if="styleCategoryLoading" class="py-6 text-center text-[12px] text-gray-500">
          加载中…
        </div>
        <div class="grid grid-cols-3 sm:grid-cols-4 gap-3">
          <button
            type="button"
            class="rounded-2xl border border-white/[0.10] bg-white/[0.04] hover:bg-white/[0.07] aspect-[3/4] flex flex-col items-center justify-center gap-2 text-gray-300"
            title="上传风格"
            @click="triggerStyleUploadPick"
          >
            <span
              class="w-11 h-11 rounded-2xl bg-white/[0.06] border border-white/[0.10] flex items-center justify-center"
            >
              <i class="fas fa-plus text-lg text-gray-300"></i>
            </span>
            <span class="text-[12px] font-semibold">上传风格</span>
          </button>
          <input
            ref="styleUploadInput"
            type="file"
            tabindex="-1"
            class="sr-only-input"
            accept="image/*"
            @change="onStyleUploadSelected"
          />

          <button
            v-for="s in filteredStyles"
            :key="s.id"
            type="button"
            class="rounded-2xl border transition-colors overflow-hidden aspect-[3/4] text-left"
            :class="
              selectedStyleLocal && selectedStyleLocal.id === s.id
                ? 'border-fuchsia-500/50 bg-fuchsia-500/10'
                : 'border-white/[0.10] bg-white/[0.04] hover:bg-white/[0.07]'
            "
            :title="s.name"
            @click="pickStyle(s)"
          >
            <div class="h-full w-full p-3 flex flex-col justify-between">
              <div
                class="w-full rounded-xl bg-gradient-to-br from-white/[0.10] to-white/[0.02] border border-white/[0.10] flex-1 flex items-center justify-center overflow-hidden"
              >
                <img
                  v-if="s.thumb"
                  :src="s.thumb"
                  alt=""
                  loading="lazy"
                  class="w-full h-full object-cover opacity-95"
                />
                <span v-else class="text-[22px] font-black text-white/80">{{ s.badge || 'S' }}</span>
              </div>
              <div class="pt-2 text-center">
                <div class="text-[12px] font-bold text-gray-100 truncate">{{ s.name }}</div>
                <div
                  v-if="String(s.tab || '') !== 'mine'"
                  class="text-[10px] text-gray-400 truncate mt-0.5"
                >
                  {{ s.desc || '' }}
                </div>
              </div>
            </div>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import workflowAnchoredModalMixin from '@/mixins/workflowAnchoredModalMixin'
import {
  fetchWorkflowStyleCategoriesList,
  mergeWorkflowStyleSearchResponse,
  postWorkflowStyleSearch
} from '@/utils/workflowStyleApi'

export default {
  name: 'WorkflowStyleLibraryModal',
  mixins: [workflowAnchoredModalMixin],
  props: {
    visible: { type: Boolean, default: false },
    selectedStyle: { type: Object, default: null }
  },
  data() {
    return {
      selectedStyleLocal: null,
      styleTab: 'all',
      categoryList: [],
      categoryStyleMap: {},
      styleCategoryLoading: false,
      styleMine: [],
      styleRecent: [],
      _styleMineLoaded: false,
      _styleRecentLoaded: false,
      _styleScrollRaf: 0,
      _styleLastAutoLoadAt: 0,
      styleTabs: [
        { id: 'all', label: '全部' },
        { id: 'mine', label: '我的风格' },
        { id: 'recent', label: '最近使用' }
      ],
      styleOptions: []
    }
  },
  computed: {
    styleCategoryTabs() {
      const list = Array.isArray(this.categoryList) ? this.categoryList : []
      if (list.length > 0) {
        const fixed = [
          { id: 'all', label: '全部' },
          { id: 'mine', label: '我的风格' },
          { id: 'recent', label: '最近使用' }
        ]
        return fixed.concat(
          list
            .filter((x) => x && x.id != null)
            .map((x) => ({
              id: String(x.id),
              label: String(x.name || x.title || x.label || '未命名')
            }))
        )
      }
      return this.styleTabs
    },
    shouldShowStyleLoadMore() {
      const hasCats = Array.isArray(this.categoryList) && this.categoryList.length > 0
      if (!hasCats) return false
      const tab = String(this.styleTab || '')
      if (!tab || tab === 'mine' || tab === 'recent' || tab === 'ip') return false
      const cur = this.categoryStyleMap && this.categoryStyleMap[tab]
      if (!cur) return true
      return !cur.finished
    },
    filteredStyles() {
      const tab = String(this.styleTab || 'all')
      const mine = Array.isArray(this.styleMine) ? this.styleMine : []
      const recent = Array.isArray(this.styleRecent) ? this.styleRecent : []
      const hasCats = Array.isArray(this.categoryList) && this.categoryList.length > 0
      if (tab === 'mine') return mine
      if (tab === 'recent') return recent
      if (!hasCats) return mine
      const bucket = this.categoryStyleMap && this.categoryStyleMap[tab]
      return bucket && Array.isArray(bucket.list) ? bucket.list : []
    },
    panelPositionStyle() {
      return this.buildPanelPositionStyle()
    }
  },
  watch: {
    visible(v) {
      if (v) this.onOpen()
    },
    selectedStyle: {
      immediate: true,
      handler(s) {
        this.selectedStyleLocal = s ? { ...s } : null
      }
    }
  },
  methods: {
    getPanelLayoutOptions() {
      return { panelWidth: 520, panelMaxHeight: 480, gap: 10, align: 'left' }
    },
    onOpen() {
      this.loadStyleRecentFromStorage()
      this.fetchStyleCategories()
      this.$nextTick(() => {
        const el = this.$refs.styleLibraryScroll
        if (el) el.scrollTop = 0
      })
    },
    loadStyleMineFromStorage() {
      if (this._styleMineLoaded) return
      this._styleMineLoaded = true
      try {
        const raw = localStorage.getItem('cw_style_mine') || '[]'
        const arr = JSON.parse(raw)
        this.styleMine = Array.isArray(arr) ? arr.filter((x) => x && x.id && x.name) : []
      } catch (e) {
        this.styleMine = []
      }
    },
    loadStyleRecentFromStorage() {
      if (this._styleRecentLoaded) return
      this._styleRecentLoaded = true
      try {
        const raw = localStorage.getItem('cw_recent_styles') || '[]'
        const arr = JSON.parse(raw)
        this.styleRecent = Array.isArray(arr) ? arr.filter((x) => x && x.id && x.name) : []
      } catch (e) {
        this.styleRecent = []
      }
    },
    fetchStyleCategories() {
      fetchWorkflowStyleCategoriesList()
        .then((list) => {
          this.categoryList = list
          if (!this.categoryStyleMap.all) {
            this.fetchCategoryStyles({ categoryId: 'all', reset: true })
          }
        })
        .catch(() => {
          this.categoryList = []
        })
    },
    fetchCategoryStyles({ categoryId, reset }) {
      const cid = categoryId != null ? String(categoryId) : ''
      if (!cid || this.styleCategoryLoading) return
      const cur = (this.categoryStyleMap && this.categoryStyleMap[cid]) || null
      if (cur && cur.finished && !reset) return

      this.styleCategoryLoading = true
      const nextPage = reset ? 1 : Math.max(1, parseInt(cur && cur.page, 10) || 1)

      postWorkflowStyleSearch({ categoryId: cid, page: nextPage })
        .then((res) => {
          const merged = mergeWorkflowStyleSearchResponse(res, {
            categoryId: cid,
            reset,
            previousBucket: cur
          })
          this.$set(this.categoryStyleMap, cid, merged)
        })
        .catch(() => {})
        .finally(() => {
          this.styleCategoryLoading = false
        })
    },
    onStyleCategoryTabClick(t) {
      const id = t && t.id != null ? String(t.id) : ''
      this.styleTab = id || 'all'
      if (id === 'mine') {
        this.loadStyleMineFromStorage()
        return
      }
      if (id === 'recent') {
        this.loadStyleRecentFromStorage()
        return
      }
      if (id === 'all') {
        this.fetchCategoryStyles({ categoryId: 'all', reset: false })
        return
      }
      if (id && id !== 'mine' && id !== 'recent') {
        this.fetchCategoryStyles({ categoryId: id, reset: false })
      }
    },
    loadMoreCategoryStyles() {
      const id = String(this.styleTab || '')
      if (!id || id === 'mine' || id === 'recent') return
      this.fetchCategoryStyles({ categoryId: id, reset: false })
    },
    onStyleLibraryScroll() {
      if (this._styleScrollRaf) return
      this._styleScrollRaf = requestAnimationFrame(() => {
        this._styleScrollRaf = 0
        if (!this.shouldShowStyleLoadMore || this.styleCategoryLoading) return
        const el = this.$refs.styleLibraryScroll
        if (!el) return
        const remain = (el.scrollHeight || 0) - ((el.scrollTop || 0) + (el.clientHeight || 0))
        if (remain > 160) return
        const now = Date.now()
        if (now - (this._styleLastAutoLoadAt || 0) < 600) return
        this._styleLastAutoLoadAt = now
        this.loadMoreCategoryStyles()
      })
    },
    pickStyle(s) {
      this.selectedStyleLocal = s ? { ...s } : null
      this.pushStyleRecent(s)
      this.$emit('select', s)
      this.$emit('close')
    },
    clearStyle() {
      this.selectedStyleLocal = null
      this.$emit('select', null)
    },
    pushStyleRecent(s) {
      if (!s) return
      try {
        const raw = localStorage.getItem('cw_recent_styles') || '[]'
        let arr = JSON.parse(raw)
        if (!Array.isArray(arr)) arr = []
        arr = [s].concat(arr.filter((x) => x && x.id !== s.id)).slice(0, 24)
        localStorage.setItem('cw_recent_styles', JSON.stringify(arr))
        this.styleRecent = arr.slice(0, 12)
      } catch (e) {
        /* ignore */
      }
    },
    triggerStyleUploadPick() {
      const input = this.$refs.styleUploadInput
      if (!input) return
      try {
        input.value = ''
      } catch (e) {
        /* ignore */
      }
      input.click()
    },
    onStyleUploadSelected(e) {
      const file = e && e.target && e.target.files ? e.target.files[0] : null
      if (!file || !String(file.type || '').startsWith('image/')) return
      const reader = new FileReader()
      reader.onload = () => {
        const dataUrl = typeof reader.result === 'string' ? reader.result : ''
        if (!dataUrl.startsWith('data:image/')) return
        const id = `mine-${Date.now()}`
        const style = {
          id,
          tab: 'mine',
          name: `我的风格${(this.styleMine || []).length + 1}`,
          badge: 'M',
          desc: '',
          thumb: dataUrl,
          refDataUrl: dataUrl,
          prompt: ''
        }
        this.styleMine = [style].concat(this.styleMine || []).slice(0, 30)
        try {
          localStorage.setItem('cw_style_mine', JSON.stringify(this.styleMine))
        } catch (err) {
          /* ignore */
        }
        this.pickStyle(style)
      }
      reader.readAsDataURL(file)
    }
  }
}
</script>

<style scoped>
.plus-wf-anchor-layer {
  position: fixed;
  inset: 0;
  z-index: 5000;
  pointer-events: none;
}
.plus-wf-anchor-layer__backdrop {
  position: fixed;
  inset: 0;
  pointer-events: auto;
  background: rgba(0, 0, 0, 0.12);
}
.plus-wf-anchor-layer__panel {
  position: fixed;
  display: flex;
  flex-direction: column;
  pointer-events: auto;
  overflow: hidden;
  border-radius: 24px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(27, 27, 31, 0.98);
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.65);
}
.sr-only-input {
  position: absolute;
  left: -9999px;
  width: 1px;
  height: 1px;
  opacity: 0;
}
</style>
